<template>
    <DropdownMenu :modal="false" @update:open="handleNotificationsOpen">
        <DropdownMenuTrigger as-child>
            <button
                v-if="variant === 'icon'"
                type="button"
                class="relative flex items-center text-foreground transition-colors hover:text-new-topol-blue"
                aria-label="Notifications"
            >
                <span
                    v-if="hasUnreadNotifications"
                    class="flex h-4 min-w-4 items-center justify-center bg-new-topol-blue px-1 text-xs font-medium leading-none text-white"
                >
                    {{ unreadCount }}
                </span>
                <span class="flex items-center justify-center p-2">
                    <component
                        :is="hasUnreadNotifications ? NotificationNewIcon : NotificationIcon"
                        class="size-4"
                    />
                </span>
            </button>

            <button
                v-else
                type="button"
                class="flex w-full items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
                <span class="relative flex">
                    <component
                        :is="hasUnreadNotifications ? NotificationNewIcon : NotificationIcon"
                        class="size-5 text-muted-foreground"
                    />
                </span>
                Notifications
                <span
                    v-if="hasUnreadNotifications"
                    class="ml-auto flex h-4 min-w-4 items-center justify-center bg-new-topol-blue px-1 text-xs font-medium leading-none text-white"
                >
                    {{ unreadCount }}
                </span>
            </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
            :align="align"
            :side="side"
            class="w-[calc(100vw-40px)] p-0 md:w-96"
            data-cy="notifications-menu"
        >
            <div class="flex items-center justify-between border-b border-border bg-new-neutral-50 px-4 py-3">
                <p class="text-sm font-semibold text-foreground">Messages & events</p>
            </div>

            <div v-if="notificationsLoading" class="flex justify-center py-8">
                <Loader2Icon class="size-5 animate-spin text-muted-foreground" />
            </div>

            <ul v-else-if="notifications.length > 0" class="max-h-[480px] overflow-y-auto">
                <li
                    v-for="n in notifications"
                    :key="n.id"
                    class="flex gap-3 border-t border-border px-4 py-3 transition-colors first:border-t-0 hover:bg-muted/50"
                >
                    <span
                        class="mt-1.5 size-2 shrink-0 rounded-full"
                        :class="n.isRead ? 'border border-border bg-transparent' : 'bg-new-blue-500'"
                    ></span>
                    <div class="min-w-0 flex-1">
                        <span
                            class="inline-block px-2 py-[2px] text-[10px] min-[1920px]:text-[12px] font-medium capitalize text-foreground"
                            :class="{
                                'bg-new-blue-100': n.type === 'info',
                                'bg-new-neutral-200': n.type === 'report',
                                'bg-new-orange-200': n.type === 'warning',
                                'bg-new-red-200': n.type === 'critical',
                                'bg-new-violet-100': n.type === 'news',
                            }"
                        >
                            {{ n.type }}
                        </span>
                        <p
                            class="mt-1 text-sm font-semibold"
                            :class="n.isRead ? 'text-muted-foreground' : 'text-foreground'"
                        >
                            {{ n.title }}
                        </p>
                        <p
                            v-if="n.data"
                            class="mt-0.5 text-xs text-muted-foreground"
                            v-html="sanitizeHtml(n.data)"
                        ></p>
                        <a
                            v-if="n.button_text && n.button_url"
                            :href="n.button_url"
                            target="_blank"
                            class="mt-2 inline-block text-xs font-semibold text-new-blue-500 hover:underline"
                        >
                            {{ n.button_text }}
                        </a>
                        <p class="mt-1 text-[10px] min-[1920px]:text-[12px] text-muted-foreground">
                            {{ formatNotificationTime(n.created_at) }}
                        </p>
                    </div>
                </li>
            </ul>

            <div v-else class="px-4 py-8 text-center">
                <p class="text-sm text-muted-foreground">You have no new messages.</p>
            </div>

            <button
                v-if="canFetchMoreNotifications && notifications.length > 0 && !notificationsLoading"
                type="button"
                class="w-full border-t border-border bg-new-neutral-50 py-3 text-xs font-medium text-new-blue-500 transition-colors hover:bg-new-neutral-100"
                @click="loadMoreNotifications"
            >
                Show older
            </button>
        </DropdownMenuContent>
    </DropdownMenu>
</template>

<script lang="ts" setup>
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Loader2Icon } from 'lucide-vue-next';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import NotificationNewIcon from './icons/NotificationNewIcon.vue';
import NotificationIcon from './icons/NotificationIcon.vue';
import IAnnouncement from '@/types/Announcements/IAnnouncement';
import { formatDistance } from 'date-fns';

withDefaults(
    defineProps<{
        variant?: 'icon' | 'row';
        align?: 'start' | 'center' | 'end';
        side?: 'top' | 'right' | 'bottom' | 'left';
    }>(),
    {
        variant: 'icon',
        align: 'end',
        side: 'bottom',
    }
);

const hasUnreadNotifications = ref(false);
const unreadCount = ref(0);
const notificationsLoading = ref(false);
const notificationsLoaded = ref(false);

const {
    data: notifications,
    fetchData: fetchNotifications,
    currentPage: notificationsPage,
    canFetchMore: canFetchMoreNotifications,
} = useResource<IAnnouncement>('/announcements-notifications', {
    perPage: 10,
    hasInfiniteScroll: true,
});

const fetchUnreadStatus = async () => {
    try {
        const response = await axios.get(route('user.has-unread-announcements.index'));
        if (response.data.success) {
            hasUnreadNotifications.value = response.data.data;
            unreadCount.value = response.data.count ?? 0;
        }
    } catch {
        // silently ignore — bell still works without the badge
    }
};

const markAllRead = async () => {
    try {
        await axios.patch(route('announcements-notifications.update'));
    } finally {
        hasUnreadNotifications.value = false;
        unreadCount.value = 0;
    }
};

const handleNotificationsOpen = async (open: boolean) => {
    if (!open) return;
    if (!notificationsLoaded.value) {
        notificationsLoading.value = true;
        try {
            await fetchNotifications();
            notificationsLoaded.value = true;
        } finally {
            notificationsLoading.value = false;
        }
    }
    if (hasUnreadNotifications.value) {
        await markAllRead();
    }
};

const loadMoreNotifications = async () => {
    notificationsPage.value += 1;
    await fetchNotifications();
};

const formatNotificationTime = (createdAt: string): string => {
    return formatDistance(new Date(createdAt), new Date(), { addSuffix: true });
};

onMounted(() => {
    fetchUnreadStatus();
});
</script>
